import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import type { z } from 'zod';

import { PermissionError, SessionExpiredError, StoreDisabledError } from './api-errors';

export class FetchWrapper {
  tokenAccessor: Auth0ContextInterface['getAccessTokenSilently'];

  constructor(tokenAccessor: Auth0ContextInterface['getAccessTokenSilently']) {
    this.tokenAccessor = tokenAccessor;
  }

  get(url: string) {
    const requestOptions = {
      method: 'GET',
    };

    return this.doFetch(url, requestOptions);
  }

  post(url: string, body: unknown, options: { headers?: Record<string, string> } = {}) {
    return this.jsonRequest('POST', url, body, options.headers);
  }

  put(url: string, body: unknown) {
    return this.jsonRequest('PUT', url, body);
  }

  patch(url: string, body: unknown) {
    return this.jsonRequest('PATCH', url, body);
  }

  // Shared by post/put/patch: every JSON-body verb wants the same
  // {method, Content-Type header, stringified body} shape, differing only in
  // the verb itself (and post's caller-supplied extra headers, e.g. the
  // receiving-session claim's X-Tab-ID).
  private jsonRequest(
    method: string,
    url: string,
    body: unknown,
    headers?: Record<string, string>
  ) {
    const requestOptions = {
      method,
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    };

    return this.doFetch(url, requestOptions);
  }

  delete(url: string) {
    const requestOptions = {
      method: 'DELETE',
    };

    return this.doFetch(url, requestOptions);
  }

  async doFetch(url: string, options: RequestInit) {
    const token = await this.tokenAccessor();

    const requestHeaders: Headers = new Headers(options.headers);
    requestHeaders.set('Authorization', `Bearer ${token}`);
    options.headers = requestHeaders;

    const response = await fetch(url, options);

    // The backend distinguishes authentication from authorization: 401 means the
    // session/token is gone (auth layer), 403 means the user is signed in but not
    // allowed. Throw typed errors so the global cache handler can present each one
    // consistently, regardless of which endpoint or hook made the request.
    if (response.status === 401) {
      throw new SessionExpiredError();
    }

    if (response.status === 403) {
      throw new PermissionError();
    }

    // 402 means the store's account is not in good standing — an expected state,
    // not a defect. Throwing here (rather than after parsing) is what keeps it
    // out of throwIfNotOk's datadogRum.addError call.
    if (response.status === 402) {
      throw new StoreDisabledError();
    }

    return response;
  }
}

/**
 * A failed response's parsed body, as far as we can claim to know it.
 *
 * Our own handlers always emit `message`, and `code` when the failure has a
 * stable machine-readable identifier (`lambdas/api/api.go` errorBody), plus
 * whatever payload the endpoint merged in — today only the order the v2
 * conflict hands back for the client to rebase onto.
 *
 * Every field is optional anyway, because not every failure gets that far:
 * API Gateway, WAF, and proxy failures answer with their own shapes, and
 * `handleVoidResponse` passes `null` outright when the body doesn't parse.
 * Nothing validates this either — callers pass the raw `any` from
 * `response.json()`, so a required `message` would be an unchecked promise,
 * and `throwIfNotOk`'s `|| response.statusText` fallback (which exists for
 * precisely this case) would read as dead code.
 *
 * So: typed rather than `unknown` to spare consumers a cast, optional
 * throughout to stay honest about what is actually guaranteed.
 */
export type ApiErrorBody = { message?: string; code?: string; order?: unknown } | null;

/**
 * Thrown by every handler below when the server answers !ok.
 *
 * Carries the status and the parsed body because some endpoints answer a
 * failure with a payload the caller needs: the v2 order-edit endpoints answer
 * an exhausted version conflict with 409 + `{message, code, order}`, where
 * `order` is the server's current truth to rebase pending edits onto. While
 * these handlers discarded the body, a caller that needed it had to bypass them
 * entirely and re-implement the whole parse/validate path.
 *
 * `message` keeps the historical `Invalid response: ` prefix — two call sites
 * strip it and show the remainder to the user (`hooks/uploads.ts`,
 * `pages/receiving/components/companion-picker-step.tsx`).
 */
export class ApiResponseError extends Error {
  status: number;
  body: ApiErrorBody;

  constructor(message: string, status: number, body: ApiErrorBody = null) {
    super(message);
    this.name = 'ApiResponseError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The one not-ok check for endpoints whose body the caller reads itself (or
 * ignores): raise the same ApiResponseError the schema handlers raise, and
 * otherwise do nothing. A body that isn't JSON is not an error here — an empty
 * 204 is a normal success.
 *
 * Prefers the backend's `{"message": "..."}` so screens can show actionable
 * copy — e.g. the 409 "companion is not connected" from receiving's /resume and
 * /abandon — and falls back to `<action> failed: <status> <statusText>`, so
 * `action` should name the operation as the user would ("cancel", "Download").
 *
 * Unlike the schema handlers this does NOT report to Datadog. These are
 * explicit user actions whose caller already decides what a failure means —
 * an inline message, a warning toast for an expected 409, a reported error —
 * so reporting here would double up on every one of them.
 */
export const handleVoidResponse = async (response: Response, action: string): Promise<void> => {
  if (response.ok) {
    return;
  }

  let message = `${action} failed: ${response.status} ${response.statusText}`;
  let body: ApiErrorBody = null;
  try {
    body = (await response.json()) as ApiErrorBody;
    if (body?.message) {
      message = body.message;
    }
  } catch {
    // Non-JSON error body — keep the status-line message.
  }

  throw new ApiResponseError(message, response.status, body);
};

// The two schema handlers every service response goes through: single → an
// invalid payload is fatal; multiple → invalid rows are dropped with a warning
// and reported to Datadog, valid rows come back parsed, a non-array is fatal.
// Unchanged by the rename off createZod* — that name only ever marked the
// coexistence period while Yup schemas were still around.
/**
 * `report: false` drops the Datadog report for a not-ok response, leaving the
 * rejection itself untouched.
 *
 * This is `handleVoidResponse`'s reasoning — an explicit user action whose
 * caller already turns a failure into user-facing copy owns that decision, and
 * reporting here doubles up on it — made available to a call that needs its
 * payload parsed and so cannot use the void handler. The first is
 * `triggerUpload` (services/uploads.ts): a store owner pressing Trigger Upload
 * while one is running gets a 409 naming the busy machine, which the uploads
 * hook shows as a toast. That is the feature working, not a defect to report.
 *
 * Only reach for it when a failure is genuinely expected and already surfaced.
 * The default stays `true` so a call has to opt out deliberately.
 */
type SchemaHandlerOptions = { report?: boolean };

export const createSingleHandler = <T extends z.ZodType>(
  schema: T,
  { report = true }: SchemaHandlerOptions = {}
) => {
  return (response: Response) => handleSingleResponse(response, schema, report);
};

// No `report` option here yet: every list endpoint's failure is a genuine
// defect, and none of them turn one into user-facing copy the way the single
// handler's opt-out exists for. Add it when one does.
export const createMultipleHandler = <T extends z.ZodType>(schema: T) => {
  return (response: Response) => handleMultipleResponse(response, schema);
};

const throwIfNotOk = (response: Response, json: ApiErrorBody, report = true) => {
  if (!response.ok) {
    const error = new ApiResponseError(
      `Invalid response: ${json?.message || response.statusText}`,
      response.status,
      json
    );
    if (report) {
      datadogRum.addError(error);
    }

    throw error;
  }
};

const handleSingleResponse = <T extends z.ZodType>(
  response: Response,
  schema: T,
  report = true
) => {
  return response.json().then((json) => {
    throwIfNotOk(response, json, report);

    return schema.parse(json);
  });
};

const handleMultipleResponse = <T extends z.ZodType>(response: Response, schema: T) => {
  return response.json().then((json) => {
    throwIfNotOk(response, json);

    if (!Array.isArray(json)) {
      throw new Error('Invalid response: expected an array');
    }

    return json.flatMap((item): z.output<T>[] => {
      const result = schema.safeParse(item);
      if (!result.success) {
        console.warn(
          '[FetchWrapper] Dropping item that failed schema validation:',
          result.error,
          item
        );
        datadogRum.addError(result.error);

        return [];
      }

      return [result.data];
    });
  });
};
